import { useMemo } from "react";
import type { ValidCombinationDto } from "@normax-performance/shared-types";
import { classifyPerformanceZone, type PerformanceZoneThresholds } from "@normax-performance/shared-format";
import type { NormedRowState } from "../daily-entry.types";
import { SearchCombobox, type ComboboxOption } from "../../../shared-ui/primitives";
import { Input } from "../../../shared-ui/primitives";
import { formatCodeAndName } from "../../../lib/format";
import { useLinePreview } from "../hooks/useLinePreview";
import { resolveMatchedNorm } from "../resolve-norm";
import { RowShell } from "./RowShell";
import type { RowFocusNav } from "./EntryGrid";
import "./NormedWorkRow.css";

export interface NormedWorkRowProps {
  index: number;
  row: NormedRowState;
  validCombinations: ValidCombinationDto[];
  onChange: (patch: Partial<NormedRowState>) => void;
  onDelete: () => void;
  readOnly?: boolean;
  /**
   * Enter-to-next-field registry for this row (task:
   * docs/NORMAX_PERFORMANCE_DAILY_ENTRY_WORKLINE_NOTE_FORENSIC_REPORT.md —
   * Napomena moved into the always-visible core row, Part A/C) — real field
   * order: 0 Proizvod, 1 Operacija, 2 Vreme, 3 Ostvareno, 4 Napomena
   * (always visible), 5 Broj radnog naloga (RowShell, expand-only).
   * `undefined` in read-only rows (nothing to register).
   */
  focusNav?: RowFocusNav;
  rowRef?: React.Ref<HTMLDivElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  /** From GET /company-performance-settings/current for this entry's work_date. */
  thresholds: PerformanceZoneThresholds;
}

export function NormedWorkRow({
  index,
  row,
  validCombinations,
  onChange,
  onDelete,
  readOnly,
  focusNav,
  rowRef,
  onKeyDown,
  thresholds,
}: NormedWorkRowProps) {
  const productOptions = useMemo<ComboboxOption[]>(() => {
    const seen = new Map<string, ComboboxOption>();
    const pool = row.operationId
      ? validCombinations.filter((c) => c.operationId === row.operationId)
      : validCombinations;
    for (const c of pool) {
      if (c.productId && c.productName) {
        seen.set(c.productId, {
          id: c.productId,
          label: formatCodeAndName(c.productCode, c.productName),
          businessCode: c.productCode,
        });
      }
    }
    return [...seen.values()];
  }, [validCombinations, row.operationId]);

  const operationOptions = useMemo<ComboboxOption[]>(() => {
    const seen = new Map<string, ComboboxOption>();
    const pool = row.productId
      ? validCombinations.filter((c) => c.productId === row.productId)
      : validCombinations;
    for (const c of pool) {
      seen.set(c.operationId, {
        id: c.operationId,
        label: formatCodeAndName(c.operationCode, c.operationName),
        businessCode: c.operationCode,
      });
    }
    return [...seen.values()];
  }, [validCombinations, row.productId]);

  const matchedNorm = useMemo(
    () => resolveMatchedNorm({ productId: row.productId, operationId: row.operationId }, validCombinations),
    [validCombinations, row.operationId, row.productId],
  );

  const preview = useLinePreview(matchedNorm, row.durationMinutes, row.reportedQuantity);

  return (
    <RowShell
      rowId={row.localId}
      index={index}
      expanded={row.expanded}
      onToggleExpanded={() => onChange({ expanded: !row.expanded })}
      workOrderReference={row.workOrderReference}
      onWorkOrderReferenceChange={(v) => onChange({ workOrderReference: v })}
      onDelete={onDelete}
      readOnly={readOnly}
      rowRef={rowRef}
      onKeyDown={onKeyDown}
      workOrderReferenceFieldProps={focusNav?.field(5)}
    >
      <div className="nx-normed-row">
        {readOnly ? (
          <>
            <span className="nx-normed-row__ro nx-normed-row__product">
              {productOptions.find((o) => o.id === row.productId)?.label ?? "—"}
            </span>
            <span className="nx-normed-row__ro nx-normed-row__operation">
              {operationOptions.find((o) => o.id === row.operationId)?.label ?? "—"}
            </span>
          </>
        ) : (
          <>
            <div className="nx-normed-row__product">
              <SearchCombobox
                label="Proizvod"
                items={productOptions}
                selectedKey={row.productId}
                onSelectionChange={(id) => onChange({ productId: id })}
                required
                inputRef={focusNav?.field(0).ref}
                onKeyDown={focusNav?.field(0).onKeyDown}
              />
            </div>
            <div className="nx-normed-row__operation">
              <SearchCombobox
                label="Operacija"
                items={operationOptions}
                selectedKey={row.operationId}
                onSelectionChange={(id) => onChange({ operationId: id })}
                required
                inputRef={focusNav?.field(1).ref}
                onKeyDown={focusNav?.field(1).onKeyDown}
              />
            </div>
          </>
        )}

        <div className="nx-normed-row__num">
          <Input
            label="Vreme"
            inputMode="decimal"
            value={row.durationMinutes}
            disabled={readOnly}
            onChange={(e) => onChange({ durationMinutes: e.target.value })}
            ref={focusNav?.field(2).ref}
            onKeyDown={focusNav?.field(2).onKeyDown}
          />
        </div>
        <div className="nx-normed-row__num">
          <Input
            label="Ostvareno"
            inputMode="decimal"
            value={row.reportedQuantity}
            disabled={readOnly}
            onChange={(e) => onChange({ reportedQuantity: e.target.value })}
            ref={focusNav?.field(3).ref}
            onKeyDown={focusNav?.field(3).onKeyDown}
          />
        </div>
        <div className="nx-normed-row__readout tabular-nums">
          <span className="nx-normed-row__readout-label">Planirano</span>
          <span>{preview.plannedQuantityLabel ?? "—"}</span>
        </div>
        <div
          className={[
            "nx-normed-row__readout",
            "tabular-nums",
            preview.performanceRatioRaw !== null
              ? `nx-normed-row__readout--zone-${
                  classifyPerformanceZone(Number(preview.performanceRatioRaw) * 100, thresholds).zone
                }`
              : "",
          ].join(" ")}
          title={
            preview.performanceRatioRaw !== null
              ? classifyPerformanceZone(Number(preview.performanceRatioRaw) * 100, thresholds).label
              : undefined
          }
        >
          <span className="nx-normed-row__readout-label">%</span>
          <span>{preview.performanceRatioLabel ?? "—"}</span>
        </div>
      </div>
      <div className="nx-normed-row__note">
        <Input
          label="Napomena"
          value={row.note ?? ""}
          disabled={readOnly}
          onChange={(e) => onChange({ note: e.target.value })}
          ref={focusNav?.field(4).ref}
          onKeyDown={focusNav?.field(4).onKeyDown}
        />
      </div>
      {matchedNorm ? (
        <p className="nx-normed-row__norm-ref">
          Norma: {matchedNorm.standardQuantity} {matchedNorm.unitCode} / {matchedNorm.baseDurationMinutes} min
        </p>
      ) : null}
    </RowShell>
  );
}
